import { useEffect } from 'react'
import { Link, useParams, useSearchParams, useNavigate, useOutletContext } from 'react-router-dom'
import { useComputed, useSignal } from '@preact/signals-react'

import Breadcrumb from 'react-bootstrap/Breadcrumb'
import Stack from 'react-bootstrap/Stack'

import ConfirmationModal from '../../components/ConfirmationModal'
import DebugAccordion from '../../components/DebugAccordion'
import JsonForm from '../../components/JsonForm.jsx'
import Loadable from '../../components/Loadable.jsx'
import PageTitle from '../../components/PageTitle.jsx'
import UtgNav from './components/UtgNav'
import UtgTools from './components/UtgTools.jsx'

import { fetchComponentConfig, updateComponentConfig, fetchDeviceConfigRecord } from '../../state/actions/componentConfigActions.js'
import { fetchDeviceSchemas } from '../../state/actions/deviceSchemaActions.js'
import { addNotification, NotificationTypes } from '../../state/actions/notificationActions.js'
import { fetchUtg } from '../../state/actions/utgActions.js'

import { componentConfigSignal, componentConfigLoadedSignal, componentConfigErrorSignal } from '../../state/signals/componentConfigSignals.js'
import { deviceSchemasSignal, deviceSchemasLoadedSignal, deviceSchemasErrorSignal } from '../../state/signals/deviceSchemaSignals.js'
import { utgSignal, utgLoadedSignal, utgErrorSignal } from '../../state/signals/utgSignals.js'

import useRedirectOnBeIdChange from '../../hooks/useRedirectOnBeIdChange.js'
import { checkDeviceDescriptionConflicts, isEmptyObject, makeFieldsReadonly } from '../../utils/utils'
export default function UtgDeviceDetail() {

    const navigate = useNavigate();
    const { beId, componentId, name } = useParams()
    const beIdChanged = useRedirectOnBeIdChange(beId, `/be/${beId}/utgs`)
    const [searchParams] = useSearchParams()
    const debug = !!searchParams.get('debug')
    const desc = searchParams.get('desc')
    const editMode = !!desc
    const { user, config, accessToken, userPermissions, userPermissionsSummary, accordionData: sidebarAccordionData } = useOutletContext()

    useEffect(() => {
        if (userPermissionsSummary.value && !userPermissionsSummary.value.canViewUtgs) {
            addNotification('You are not authorized to view UTGs for this account.', NotificationTypes.ERROR, true)
            navigate(`/`)
        }
    }, [userPermissionsSummary.value, navigate])

    const canEditComputed = useComputed(
        () => {
            return userPermissionsSummary?.value?.canEditUtgs
        }
    )

    useEffect(() => {
        if (beIdChanged) return
        fetchUtg(beId, componentId, config, accessToken).then(() => {
            console.log(utgSignal.value.ComponentPermanentId)
            console.log(utgSignal.value.MachinePermanentId)
            fetchComponentConfig(beId, utgSignal.value.machinePermanentId, utgSignal.value.componentPermanentId, config, accessToken)
        })
        fetchDeviceSchemas(config, accessToken)
        //fetchDeviceConfigRecord(beId, componentId, deviceId, config, accessToken)
    }, [beId, beIdChanged, componentId, config, accessToken])

    const deviceComputed = useComputed(() => {
        if (componentConfigSignal.value && editMode) {
            const deviceList = componentConfigSignal.value?.config?.deviceConfig?.device ?? []
            if (deviceList.length == 0) {
                return {}
            }
            const index = componentConfigSignal.value.config.deviceConfig.device.findIndex(a => a.deviceType == name && a.taskDesc == desc)
            const currentDevice = componentConfigSignal.value.config.deviceConfig.device[index]

            if (index == -1) {
                console.log("Current device config not found")
                return null
            }

            //console.log("Current device config:")
            //console.log(currentDevice)

            const jsonFormDevice = { config: currentDevice }
            return jsonFormDevice
        }
        return null
    })
    const deviceIndexComputed = useComputed(() => {
        if (componentConfigSignal.value && editMode) {
            const deviceList = componentConfigSignal.value?.config?.deviceConfig?.device ?? []
            if (deviceList.length == 0) return -1

            const index = componentConfigSignal.value.config.deviceConfig.device.findIndex(a => a.deviceType == name && a.taskDesc == desc)
            return index
        }
        return -1
    })

    const deviceSchemaComputed = useComputed(() => {
        // deviceSchemas now contains all device schemas. Find the correct one to load into jsonForm
        // note that name/deviceType is the only common field we have between the schema and the data
        // this is passed into the page as a query parameter
        if (deviceSchemasSignal && deviceSchemasSignal.value && deviceSchemasSignal.value.length) {
            // const schemaResult = { config: deviceSchemasSignal.value.find(a => a.name == name).schemaValue }
            const schema = deviceSchemasSignal.value.find(a => a.name == name)
            const schemaResult = schema.schemaValue
            return schemaResult
        }
        return {}
    })

    const jsonFormDataComputed = useComputed(() => {
        if (!deviceSchemasLoadedSignal.value || !componentConfigLoadedSignal.value) {
            return null
        }
        else if (isEmptyObject(deviceSchemaComputed.value) ||
            isEmptyObject(componentConfigSignal.value)) {
            return
        }

        // deep clone the schema
        let clonedSchema = structuredClone(deviceSchemaComputed.value)

        // disable editing of all fields if canEditComputed is false
        if (!userPermissionsSummary?.value?.canEditUtgs) {
            clonedSchema = makeFieldsReadonly(clonedSchema)
        }

        // sets the variable on the global windows object so that shift-utils.js code can determine if we are working with a profile or config schema
        globalThis.SCHEMA_MODE = 'CONFIG'
        //console.log('Set SCHEMA_MODE to', globalThis.SCHEMA_MODE)

        // NOTE: if onSubmitValid is changed to onSubmit then the callback must also be changed to receive both errors and values
        const formData = {
            schema: clonedSchema.properties.config,
            loadedComponentConfig: componentConfigSignal.value.config,
            onSubmitValid: handleFormSubmit,
            onCancel: handleCancel,
            onFormChange: handleFormChange,
            canEditConfig: userPermissionsSummary?.value?.canEditUtgs
        }
        if (deviceComputed.value) {
            formData.value = deviceComputed.value.config
        }
        //console.log('formData from config', formData)
        return formData
    })

    const showCancelModal = useSignal(false)
    const isFormEdited = useSignal(false)
    const isFormSubmitting = useSignal(false)
    const accordionData = [
        { name: 'Device', data: deviceComputed.value },
        { name: 'Device Index', data: deviceIndexComputed.value },
        { name: 'Device Schema', data: deviceSchemaComputed.value },
        { name: 'All Device Schemas', data: deviceSchemasSignal.value },
        { name: 'Component Config', data: componentConfigSignal.value },
        { name: 'UTG Data', data: utgSignal.value },
        { name: 'Access Token', data: accessToken },
        { name: 'App Config', data: config }
    ]

    const handleFormSubmit = values => {
        // short-circuit if the handler was already called. This blocks clicking the submit a second time while already processing a previous submit
        if (isFormSubmitting.value) {
            return false
        }
        isFormSubmitting.value = true
        componentConfigLoadedSignal.value = false
        try {
            //console.log('values', values)
            if (values) {

                // build the full config data including apiInterface and deviceConfig data to save back to server
                let data = {
                    config: componentConfigSignal.value.config,
                    hash: componentConfigSignal.value.hash,
                    isCommitted: componentConfigSignal.value.isCommitted,
                    lastUpdatedAt: componentConfigSignal.value.lastUpdatedAt,
                    lastUpdatedBy: componentConfigSignal.value.lastUpdatedBy,
                    revisionId: componentConfigSignal.value.revisionId,
                    componentId: componentConfigSignal.value.componentId
                }

                if (editMode) {
                    // replace the existing selected apiInterface with data from values 
                    data.config.deviceConfig.device[deviceIndexComputed.value] = values
                }
                else {
                    // insert values into the apiInterface array
                    data.config.deviceConfig.device.push(values)
                }

                // validate taskDesc uniqueness in case jsonform validation fails
                if (checkDeviceDescriptionConflicts(data.config.deviceConfig.device)) {
                    addNotification("Unable to save due to conflict. All Devices must have a unique Device Name.", NotificationTypes.ERROR)
                    return false
                }

                //console.log(data)
                updateComponentConfig(beId, utgSignal.value.machinePermanentId, utgSignal.value.componentPermanentId, data, config, accessToken)
                    .then(() => {
                        navigate(`/be/${beId}/utgs/${componentId}/devices`)
                        addNotification('Device was successfully updated.', NotificationTypes.SUCCESS, true)
                    })
                    .catch(error => {
                        console.log(error.toString())
                        addNotification("Unable to update the Device.")
                        return false
                    })
                    .finally(() => {
                        isFormSubmitting.value = false
                        // must return false to avoid postback
                        return false
                    })
            }
        }
        catch (error) {
            console.log(error)
        }

        // return false to prevent form submit from refreshing the page
        return false
    }

    const handleCancel = () => {
        if (isFormEdited.value) {
            showCancelModal.value = true
        }
        else {
            navigate('../')
        }
    }

    const handleFormChange = () => {
        //console.log('handleFormChange')
        isFormEdited.value = true
    }

    return (
        <>
            <ConfirmationModal
                show={showCancelModal.value}
                handleCancel={() => showCancelModal.value = false}
                handleConfirm={() => navigate('../devices')}
                message={'You have unsaved changes to this form. Are you sure you want to leave and lose these changes?'}
                title={"Warning: Unsaved Changes"}
                cancelText="Continue Editing"
                confirmText="Lose Changes"
            />
            <Breadcrumb className="px-3 pt-1 bg-light">
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/utgs` }}>
                    UTG List
                </Breadcrumb.Item>
                <Breadcrumb.Item active>{
                    utgLoadedSignal.value ? <>Manage {utgSignal.value.componentNickname}</> : <>Loading...</>
                }</Breadcrumb.Item>
            </Breadcrumb>
            <Stack direction="horizontal" gap={3}>
                <PageTitle
                    title={editMode ? `Manage ${name} UTG Device` : `Add ${name} UTG Device`} canEditSignal={canEditComputed} />
                <Loadable loadedSignals={[componentConfigLoadedSignal]} errorSignals={[componentConfigErrorSignal]}>
                    <UtgTools componentConfig={componentConfigSignal.value} canCreateProfile={userPermissionsSummary?.value?.canEditProfiles} beId={beId} config={config} accessToken={accessToken}></UtgTools>
                </Loadable>
            </Stack>
            <UtgNav activeKey="3"></UtgNav>
            <Loadable loadedSignals={[componentConfigLoadedSignal, deviceSchemasLoadedSignal]} errorSignals={[componentConfigErrorSignal, deviceSchemasErrorSignal]}>
                <JsonForm data={jsonFormDataComputed.value}></JsonForm>
            </Loadable>
            <DebugAccordion data={accordionData} debug={debug} />
        </>
    )
}